/**
 * Optimized Wealthica reconnect hook using React Query mutations
 * 
 * Replaces the heavy manual implementation with clean mutation-based approach
 * Provides better UX with built-in loading states and centralized error handling
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { useWealthicaReconnectMutation } from './mutations/useWealthicaReconnectMutation';
import { useWealthicaPersistMutation } from './mutations/useWealthicaPersistMutation';
import { useTokenStorage } from './useTokenStorage';
import { logger } from '@/services/LoggingService';
import type { WealthicaConnection } from '../types';

interface UseWealthicaReconnectOptimizedProps {
  connectionData?: WealthicaConnection;
}

export function useWealthicaReconnectOptimized({ connectionData }: UseWealthicaReconnectOptimizedProps) {
  const { user } = useAuthStore();
  
  // Multi-store only
  const multiStore = useMultiWealthica2Store();
  const persistDataPreference = multiStore.persistDataPreference;
  const isConnected = multiStore.isAnyConnected();
  const connect = async (cd: WealthicaConnection, bearerToken: string) => {
    multiStore.addConnection(cd.institutionId, cd, bearerToken);
    multiStore.setActiveConnection(cd.institutionId);
  };
  
  const { getBearerToken, getConnectionData } = useTokenStorage();
  const reconnectMutation = useWealthicaReconnectMutation();
  const persistMutation = useWealthicaPersistMutation();

  const reconnectAccount = useCallback(async () => {
    if (!user || !connectionData) {
      toast.error('Missing user or connection data');
      return;
    }

    try {
      // Step 0: Check and restore store state if needed
      const storedToken = getBearerToken();
      const storedConnectionData = getConnectionData();
      
      if (storedToken && storedConnectionData && !isConnected) {
        logger.info('useWealthicaReconnectOptimized', 'Restoring store state from storage before reconnect', {
          hasToken: !!storedToken,
          hasConnectionData: !!storedConnectionData,
          storeConnected: isConnected
        });
        
        // Restore store state from storage
        connect(storedConnectionData, storedToken);
      }
      
      // Step 1: Reconnect and fetch fresh data
      const result = await reconnectMutation.mutateAsync({ connectionData });
      
      if (result.success) {
        logger.info('useWealthicaReconnectOptimized', 'Reconnect successful, updating store', {
          storeMode: 'multi-store',
          accountsCount: result.data?.accounts?.length || 0
        });
        // Multi-store mode - update specific connection's accounts (quiet success)
        if (result.data?.accounts && result.data.accounts.length > 0) {
          multiStore.setAccountsForInstitution(connectionData.institutionId, result.data.accounts);
        } else {
          // Try to fetch accounts for all connections (multi-store doesn't have per-connection fetch)
          await multiStore.fetchAccountsForAllConnections();
        }
        
        // Step 2: Persist the fresh data only if user wants data saved and we have accounts
        if (result.data?.accounts && persistDataPreference && result.data.accounts.length > 0) {
          logger.info('useWealthicaReconnectOptimized', 'Persisting data per user preference');
          await persistMutation.mutateAsync({
            userId: user.id,
            connectionData,
            accounts: result.data.accounts
          });
        } else {
          logger.info('useWealthicaReconnectOptimized', 'Skipping data persistence', {
            persistEnabled: persistDataPreference,
            accountsCount: result.data?.accounts?.length || 0
          });
        }
        
        // Notify listeners to invalidate any dependent views
        try {
          const { dispatchConnectionChangeEvent } = await import('../utils/connection-utils');
          dispatchConnectionChangeEvent();
        } catch {}
      } else {
        // Handle specific error cases with appropriate user messages
        const errorMessage = result.message || 'Failed to refresh account';
        if (result.status === 'unauthorized') {
          toast.error('Your session has expired. Please reconnect your account.');
        } else if (result.status === 'not_found') {
          toast.error('Account not found. Please reconnect your account.');
        } else {
          toast.error(errorMessage);
        }
        
        // Don't update store data on failure - preserve existing cached data
        logger.warn('useWealthicaReconnectOptimized', 'Refresh failed, preserving cached data', {
          status: result.status,
          message: result.message
        });
      }
    } catch (error) {
      toast.error('Failed to refresh account. Please try again.');
    }
  }, [user, connectionData, reconnectMutation, persistMutation, persistDataPreference, getBearerToken, getConnectionData, isConnected, connect]);

  return {
    reconnectAccount,
    isRefreshing: reconnectMutation.isPending || persistMutation.isPending,
    isReconnecting: reconnectMutation.isPending,
    isPersisting: persistMutation.isPending,
    error: reconnectMutation.error || persistMutation.error
  };
}
